import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import { requestDropdown } from 'components/shared/maintenanceDropdownValues';
import { getMaintenanceCategories, getMaintenanceLocations } from 'actions/maintenanceActions';
import Row from 'components/ui/Row';
import Spacer from 'components/ui/Spacer';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import { featureEnabled } from 'helpers/ToggleBot';

import CategorySelect from './TicketDetailSections/CategorySelect';
import CompletionDateTime from './TicketDetailSections/CompletionDateTime';
import CreatedAtDateTime from './TicketDetailSections/CreatedAtDateTime';
import DescriptionInput from './TicketDetailSections/DescriptionInput';
import EntryDate from './TicketDetailSections/EntryDate';
import CompleteBy from './TicketDetailSections/CompleteBy';
import ExternalPoNumber from './TicketDetailSections/ExternalPoNumber';
import HideUpdatesSelect from './TicketDetailSections/HideUpdatesSelect';
import PermissionToAbsentEnterSelect from './TicketDetailSections/PermissionToAbsentEnterSelect';
import PermissionToEnterSelect from './TicketDetailSections/PermissionToEnterSelect';
import PrioritySelect from './TicketDetailSections/PrioritySelect';
import RequestTypeSelect from './TicketDetailSections/RequestTypeSelect';
import StatusInput from './TicketDetailSections/StatusInput';
import SubjectInput from './TicketDetailSections/SubjectInput';
import TenantDamaged from './TicketDetailSections/TenantDamaged';
import Subcategory from './TicketDetailSections/Subcategory';
import Classification from './TicketDetailSections/Classification';
import AdditionalDetails from './TicketDetailSections/AdditionalDetails';

const StyledSectionContainer = styled.div`
  margin-bottom: 32px;
`;

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const TicketDetails = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  getMaintenanceCategories,
  getMaintenanceLocations,
  isAdmin,
  canChangeTicketStatus,
  setValues,
  viewDetails,
  maintenanceCategories,
  readOnly,
}) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // runes on property change to fetch property's maintenance ticket categories
    getMaintenanceCategories(values.property_id);
    getMaintenanceLocations(values.property_id);
  }, [values.property_id, getMaintenanceCategories, getMaintenanceLocations]);

  useEffect(() => {
    let fetchedCategories = maintenanceCategories;
    if (values.property_id && values.unit_id) {
      // runs on unit change and filters through already fetched maintenanceCategory
      fetchedCategories = fetchedCategories.filter((category) =>
        ['suite', 'all_locations'].includes(category.location)
      );
      setCategories(fetchedCategories);
    } else {
      // runs on property change and filters through newly fetched maintenanceCategory from line 57 on this file
      fetchedCategories = fetchedCategories.filter((category) =>
        ['common_area', 'all_locations'].includes(category.location)
      );
    }

    if (
      viewDetails &&
      maintenanceCategories.length > 0 &&
      !maintenanceCategories.find((res) => res.value === values.category)
    ) {
      // If mode is "edit" (viewDetails) and from the latest fetched maintenanceCategories
      // if ticket's category is not included in the latest fetched maintenanceCategories,
      // update the form value category to empty string
      // Cannot do undefined here as it will not trigger validation on the field
      // so we set to undefined when we set value to the actual field if category is an empty string
      // Refer to where we set the category field value in this file.
      setFieldValue('category', '');
    }
    setCategories(fetchedCategories);
  }, [
    maintenanceCategories,
    values.property_id,
    values.unit_id,
    values.category,
    viewDetails,
    setFieldValue,
  ]);

  return (
    <StyledSectionContainer>
      <Spacer mb={0.3}>
        <FormTitle>
          {getTranslatedString('landlordPortal.maintenance.tickets.ticketDetails')}
        </FormTitle>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <StatusInput
            setFieldValue={setFieldValue}
            value={values.status}
            errors={errors}
            touched={touched}
            isAdmin={isAdmin}
            viewDetails={viewDetails}
            disabled={readOnly || !canChangeTicketStatus}
          />
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <SubjectInput
            value={values.subject}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            disabled={readOnly}
          />
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <DescriptionInput
            value={values.ticket_description}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            disabled={readOnly}
          />
        </Row>
      </Spacer>
      {viewDetails && values.tenant_note && (
        <Spacer mb={1}>
          <Row gutter={16}>
            <AdditionalDetails value={values.tenant_note} />
          </Row>
        </Spacer>
      )}
      <Spacer mb={1}>
        <Row gutter={16}>
          <CategorySelect
            setFieldValue={setFieldValue}
            value={values.category}
            categories={categories}
            errors={errors}
            touched={touched}
            disabled={!values.property_id || readOnly}
          />
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <CreatedAtDateTime
            setFieldValue={setFieldValue}
            value={values.created_at}
            errors={errors}
            touched={touched}
            disabled={readOnly}
          />
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <PrioritySelect
            setFieldValue={setFieldValue}
            value={values.priority}
            errors={errors}
            touched={touched}
            disabled={readOnly}
          />
          <RequestTypeSelect
            setFieldValue={setFieldValue}
            value={values.request_type}
            requestTypes={requestDropdown}
            errors={errors}
            touched={touched}
            disabled={readOnly}
          />
        </Row>
      </Spacer>
      <Spacer mb={1}>
        <Row gutter={16}>
          <RenderIfToggleOn toggle="ui.create_service_request.permission_to_enter">
            <PermissionToEnterSelect
              setFieldValue={setFieldValue}
              value={values.permission_to_enter}
              errors={errors}
              touched={touched}
              disabled={readOnly}
            />
          </RenderIfToggleOn>
          <HideUpdatesSelect
            setFieldValue={setFieldValue}
            value={values.hide_from_tenant}
            errors={errors}
            touched={touched}
            disabled={readOnly}
            fullWidthColumn={!featureEnabled('ui.create_service_request.permission_to_enter')}
          />
        </Row>
      </Spacer>
      {viewDetails && (
        <Spacer mb={1}>
          <Row gutter={16}>
            <Subcategory
              handleChange={handleChange}
              value={values.subcategory}
              errors={errors}
              touched={touched}
              disabled={readOnly}
            />
            <Classification
              setFieldValue={setFieldValue}
              value={values.classification}
              errors={errors}
              touched={touched}
              disabled={readOnly}
            />
          </Row>
        </Spacer>
      )}
      <RenderIfToggleOn toggle="ui.maintenance.show_external_po_number_field">
        <Spacer mb={1}>
          <Row gutter={16}>
            <ExternalPoNumber value={values.external_po_number} handleChange={handleChange} />
          </Row>
        </Spacer>
      </RenderIfToggleOn>

      <Spacer mb={1}>
        <Row gutter={16}>
          <RenderIfToggleOn toggle="ui.create_service_request.consent_to_entry_while_away">
            <PermissionToAbsentEnterSelect
              setFieldValue={setFieldValue}
              value={values.consent_to_entry_while_away}
              errors={errors}
              touched={touched}
              disabled={readOnly}
            />
          </RenderIfToggleOn>
        </Row>
      </Spacer>

      {viewDetails && (
        <React.Fragment>
          <Spacer mb={1}>
            <Row gutter={16}>
              <RenderIfToggleOn toggle="maintenance.scheduling">
                <EntryDate
                  setFieldValue={setFieldValue}
                  value={values.scheduled_at}
                  errors={errors}
                  touched={touched}
                  disabled={readOnly}
                />
              </RenderIfToggleOn>
              {['Fixed', 'Closed'].includes(values.status) && (
                <CompletionDateTime
                  setFieldValue={setFieldValue}
                  value={values.completion_date}
                  errors={errors}
                  touched={touched}
                  disabled={readOnly}
                />
              )}
              <RenderIfToggleOn toggle="maintenance.vendor_statuses_and_notifications">
                <CompleteBy
                  setFieldValue={setFieldValue}
                  value={values.complete_by}
                  errors={errors}
                  touched={touched}
                  disabled={readOnly}
                />
              </RenderIfToggleOn>
            </Row>
          </Spacer>
          <TenantDamaged
            setFieldValue={setFieldValue}
            value={values.is_tenant_damaged}
            disabled={!viewDetails || readOnly}
          />
        </React.Fragment>
      )}
    </StyledSectionContainer>
  );
};

const mapStoreToProps = (store) => {
  return {
    isAdmin: store.userTypes.admin,
    canChangeTicketStatus: store.userTypes.canChangeTicketStatus,
    maintenanceCategories: store.maintenanceReducer.maintenanceCategories,
  };
};

const mapDispatchToProps = {
  getMaintenanceCategories,
  getMaintenanceLocations,
};

export default connect(mapStoreToProps, mapDispatchToProps)(TicketDetails);
